import { InventorySession, InventoryItem, StockAlert, StockMovement } from '../types';
import { productService } from './productService';

// Mock data
const mockInventorySessions: InventorySession[] = [
  {
    id: 'inv-1',
    name: 'Inventaire mensuel Décembre 2024',
    scope: 'all',
    status: 'completed',
    createdBy: '2',
    validatedBy: '2',
    createdAt: '2024-12-01T08:00:00Z',
    validatedAt: '2024-12-01T16:00:00Z',
    items: [
      {
        id: 'inv-item-1',
        productId: 'prod-1',
        theoreticalStock: 150,
        countedStock: 148,
        variance: -2,
        comment: 'Écart mineur',
        countedBy: '2',
        countedAt: '2024-12-01T10:00:00Z'
      }
    ]
  }
];

class InventoryService {
  async getInventorySessions(): Promise<InventorySession[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return [...mockInventorySessions].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  async getInventorySession(id: string): Promise<InventorySession | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockInventorySessions.find(s => s.id === id) || null;
  }

  async createInventorySession(session: Omit<InventorySession, 'id' | 'createdAt' | 'items'>): Promise<InventorySession> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Récupérer les produits selon le scope
    const products = await productService.getProducts();
    let filteredProducts = products;
    
    if (session.scope === 'category' && session.scopeValue) {
      filteredProducts = products.filter(p => p.categoryId === session.scopeValue);
    } else if (session.scope === 'location' && session.scopeValue) {
      filteredProducts = products.filter(p => p.defaultLocationId === session.scopeValue);
    }
    
    const items: InventoryItem[] = filteredProducts.map(product => ({
      id: `inv-item-${Date.now()}-${product.id}`,
      productId: product.id,
      theoreticalStock: product.currentStock,
      countedStock: 0,
      variance: 0
    }));
    
    const newSession: InventorySession = {
      ...session,
      id: `inv-${Date.now()}`,
      createdAt: new Date().toISOString(),
      items
    };
    
    mockInventorySessions.push(newSession);
    return newSession;
  }

  async updateInventoryItem(sessionId: string, itemId: string, countedStock: number, comment?: string, userId?: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const session = mockInventorySessions.find(s => s.id === sessionId);
    if (!session) throw new Error('Session d\'inventaire non trouvée');
    
    const item = session.items.find(i => i.id === itemId);
    if (!item) throw new Error('Article d\'inventaire non trouvé');
    
    item.countedStock = countedStock;
    item.variance = countedStock - item.theoreticalStock;
    item.comment = comment;
    item.countedBy = userId;
    item.countedAt = new Date().toISOString();
    
    // Mettre à jour le statut de la session
    if (session.status === 'draft') {
      session.status = 'in_progress';
    }
  }

  async validateInventorySession(sessionId: string, userId: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const session = mockInventorySessions.find(s => s.id === sessionId);
    if (!session) throw new Error('Session d\'inventaire non trouvée');
    
    // Générer les mouvements d'ajustement pour les écarts
    for (const item of session.items) {
      if (item.variance !== 0) {
        const movement: StockMovement = {
          id: `mov-inv-${Date.now()}-${item.id}`,
          productId: item.productId,
          type: 'adjustment',
          quantity: item.variance,
          reason: `Ajustement inventaire ${session.name}`,
          userId,
          createdAt: new Date().toISOString()
        };
        
        // En réalité, cela mettrait à jour le stock du produit
        // await productService.adjustStock(item.productId, item.variance, movement.reason, userId);
      }
    }
    
    session.status = 'validated';
    session.validatedBy = userId;
    session.validatedAt = new Date().toISOString();
  }

  async getStockAlerts(): Promise<StockAlert[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const products = await productService.getProducts();
    const alerts: StockAlert[] = [];
    
    for (const product of products) {
      if (product.currentStock <= product.minThreshold) {
        const suggestedQuantity = Math.max(
          product.minThreshold - product.currentStock + Math.ceil(product.minThreshold * 0.2), // 20% de marge
          1
        );
        
        alerts.push({
          productId: product.id,
          currentStock: product.currentStock,
          minThreshold: product.minThreshold,
          suggestedQuantity,
          lastMovementDate: product.updatedAt
        });
      }
    }
    
    return alerts;
  }

  async getStockActivity(filters?: {
    startDate?: string;
    endDate?: string;
    productId?: string;
    userId?: string;
    type?: string;
  }): Promise<StockMovement[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // En réalité, cela récupérerait tous les mouvements de stock
    // Pour la démo, on retourne des données mockées
    const mockMovements: StockMovement[] = [
      {
        id: 'mov-1',
        productId: 'prod-1',
        type: 'entry',
        quantity: 100,
        toLocationId: 'loc-1',
        reason: 'Réception commande fournisseur',
        userId: '2',
        createdAt: '2024-12-10T10:00:00Z'
      },
      {
        id: 'mov-2',
        productId: 'prod-1',
        type: 'exit',
        quantity: -30,
        fromLocationId: 'loc-1',
        reason: 'Sortie chantier Maison Dupont',
        userId: '3',
        createdAt: '2024-12-12T14:30:00Z'
      },
      {
        id: 'mov-3',
        productId: 'prod-2',
        type: 'adjustment',
        quantity: -2,
        reason: 'Ajustement inventaire',
        userId: '2',
        createdAt: '2024-12-01T16:00:00Z'
      }
    ];
    
    let filtered = [...mockMovements];
    
    if (filters?.startDate) {
      filtered = filtered.filter(m => m.createdAt >= filters.startDate!);
    }
    
    if (filters?.endDate) {
      filtered = filtered.filter(m => m.createdAt <= filters.endDate!);
    }
    
    if (filters?.productId) {
      filtered = filtered.filter(m => m.productId === filters.productId);
    }
    
    if (filters?.userId) {
      filtered = filtered.filter(m => m.userId === filters.userId);
    }
    
    if (filters?.type) {
      filtered = filtered.filter(m => m.type === filters.type);
    }
    
    return filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  // Helper methods
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      draft: 'Brouillon',
      in_progress: 'En cours',
      completed: 'Terminé',
      validated: 'Validé'
    };
    return labels[status] || status;
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-orange-100 text-orange-800',
      validated: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  getMovementTypeLabel(type: string): string {
    const labels: Record<string, string> = {
      entry: 'Entrée',
      exit: 'Sortie',
      adjustment: 'Ajustement',
      transfer: 'Transfert'
    };
    return labels[type] || type;
  }

  getMovementTypeColor(type: string): string {
    const colors: Record<string, string> = {
      entry: 'bg-green-100 text-green-800',
      exit: 'bg-red-100 text-red-800',
      adjustment: 'bg-blue-100 text-blue-800',
      transfer: 'bg-orange-100 text-orange-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  }
}

export const inventoryService = new InventoryService();